import { c as _c } from "react/compiler-runtime";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@repo/blacksmith-ui/tabs";
import { cn } from "@repo/blacksmith-ui/cn";
export interface TimeRangeOption {
  label: string;
  hours: number;
}
const DEFAULT_TIME_RANGE_OPTIONS: TimeRangeOption[] = [{
  label: "3h",
  hours: 3
}, {
  label: "12h",
  hours: 12
}, {
  label: "1D",
  hours: 24
}, {
  label: "2D",
  hours: 48
}, {
  label: "5D",
  hours: 120
}, {
  label: "14D",
  hours: 336
}, {
  label: "30D",
  hours: 720
}, {
  label: "3M",
  hours: 2160
}];
interface TimeRangeTabsProps {
  value: number;
  onValueChange: (hours: number) => void;
  options?: TimeRangeOption[];
  className?: string;
}
export function TimeRangeTabs(t0) {
  const $ = _c(37);
  const {
    value,
    onValueChange,
    options: t1,
    className
  } = t0;
  const options = t1 === undefined ? DEFAULT_TIME_RANGE_OPTIONS : t1;
  const listRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const triggerRefs = useRef(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      left: 0,
      width: 0,
      ready: false
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [indicator, setIndicator] = useState(t3);
  let t4;
  if ($[2] !== value) {
    t4 = value.toString();
    $[2] = value;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const activeKey = t4;
  let t5;
  if ($[4] !== activeKey) {
    t5 = () => {
      const listEl = listRef.current;
      const activeEl = triggerRefs.current.get(activeKey) ?? null;
      if (!listEl || !activeEl) {
        return;
      }
      setIndicator({
        left: activeEl.offsetLeft,
        width: activeEl.offsetWidth,
        ready: true
      });
    };
    $[4] = activeKey;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const updateIndicator = t5;
  let t6;
  if ($[6] !== updateIndicator) {
    t6 = () => {
      const listEl_0 = listRef.current;
      if (!listEl_0) {
        return;
      }
      const rafId = requestAnimationFrame(() => {
        updateIndicator();
      });
      const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => updateIndicator()) : null;
      observer?.observe(listEl_0);
      return () => {
        cancelAnimationFrame(rafId);
        observer?.disconnect();
      };
    };
    $[6] = updateIndicator;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== options || $[9] !== updateIndicator) {
    t7 = [updateIndicator, options];
    $[8] = options;
    $[9] = updateIndicator;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  useLayoutEffect(t6, t7);
  let t8;
  if ($[11] !== className) {
    t8 = cn("flex items-center", className);
    $[11] = className;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== value) {
    t9 = value.toString();
    $[13] = value;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== onValueChange) {
    t10 = val => {
      if (val) {
        onValueChange(parseInt(val, 10));
      }
    };
    $[15] = onValueChange;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = !indicator.ready && "opacity-0";
  let t12;
  if ($[17] !== t11) {
    t12 = cn("absolute left-0 top-0.5 bottom-0.5 rounded-md border", "bg-white dark:bg-[#1D1E20] border-[#C5C5C5] dark:border-[#7C7D7F]", "transition-[transform,width] duration-150 ease-out will-change-[transform,width]", t11);
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const t13 = `${indicator.width}px`;
  const t14 = `translateX(${indicator.left}px)`;
  let t15;
  if ($[19] !== t13 || $[20] !== t14) {
    t15 = {
      width: t13,
      transform: t14
    };
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t12 || $[23] !== t15) {
    t16 = <div aria-hidden={true} className={t12} style={t15} />;
    $[22] = t12;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== options) {
    t17 = options.map(t18 => {
      const {
        label,
        hours
      } = t18;
      return <TabsTrigger key={hours} value={hours.toString()} className={cn("relative z-10 h-[22px] px-2 text-xs leading-4 transition-colors rounded-md min-w-0", "border border-transparent cursor-pointer", "focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0", "data-[state=active]:bg-transparent dark:data-[state=active]:bg-transparent", "data-[state=active]:border-transparent", "data-[state=active]:text-[#161718] dark:data-[state=active]:text-white", "data-[state=active]:font-medium", "data-[state=inactive]:bg-transparent", "data-[state=inactive]:text-neutral-900 dark:data-[state=inactive]:text-neutral-200/80", "data-[state=inactive]:font-normal", "hover:text-neutral-600 dark:hover:text-neutral-300", "data-[state=active]:hover:bg-transparent")} ref={el => {
        triggerRefs.current.set(hours.toString(), el);
      }}>{label}</TabsTrigger>;
    });
    $[25] = options;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== t16 || $[28] !== t17) {
    t18 = <TabsList ref={listRef} className="relative h-auto bg-[#eeeeee] dark:bg-[#2A2C2C] rounded-lg p-0.5 gap-0">{t16}{t17}</TabsList>;
    $[27] = t16;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t10 || $[31] !== t18 || $[32] !== t9) {
    t19 = <Tabs value={t9} onValueChange={t10}>{t18}</Tabs>;
    $[30] = t10;
    $[31] = t18;
    $[32] = t9;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t19 || $[35] !== t8) {
    t20 = <div className={t8}>{t19}</div>;
    $[34] = t19;
    $[35] = t8;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  return t20;
}